import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, LogIn } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { browserLang } from "@/lib/langs";
import { LangSelect } from "@/components/LangSelect";
import { PrivacyPicker } from "@/components/room/Privacy";

export const VoicePicker = ({ value, onChange }) => (
  <div className="grid grid-cols-2 border border-border" data-testid="create-room-select-voice">
    {[{ id: "female", l: "Female · Sarah" }, { id: "male", l: "Male · Brian" }].map((v) => (
      <button type="button" key={v.id} onClick={() => onChange(v.id)} data-testid={`voice-option-${v.id}`}
        className={`h-11 text-sm transition-colors duration-200 ${value === v.id ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"}`}>
        {v.l}
      </button>
    ))}
  </div>
);

export const CreateRoomCard = () => {
  const nav = useNavigate();
  const [spoken, setSpoken] = useState(browserLang());
  const [voice, setVoice] = useState("female");
  const [encrypted, setEncrypted] = useState(false);
  const [busy, setBusy] = useState(false);

  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/rooms", { spoken_language: spoken, voice, encrypted });
      nav(`/room/${data.room_id}`, { state: { joined: true } });
    } catch (e) {
      toast.error(errMsg(e));
      setBusy(false);
    }
  };

  return (
    <div className="holo-panel corners matrix-edge p-6 sm:p-8" data-testid="create-room-card">
      <div className="eyebrow">[01] Initialise</div>
      <h2 className="mt-3 font-display text-2xl font-extrabold tracking-tight">Open a new <span className="aurora-text">SpeechFlow</span></h2>
      <div className="mt-8">
        <label className="block"><span className="mono-label">I speak</span><div className="mt-2"><LangSelect value={spoken} onChange={setSpoken} allowAuto={false} testId="create-room-select-spoken-lang" /></div></label>
        <p className="mt-2 font-mono text-[11px] text-muted-foreground" data-testid="create-room-hint">Up to 4 people. Pick each guest&apos;s language when you invite them: everyone reads and hears in their own tongue.</p>
      </div>
      <div className="mt-5"><span className="mono-label">Aurora&apos;s voice</span><div className="mt-2"><VoicePicker value={voice} onChange={setVoice} /></div></div>
      <div className="mt-5"><span className="mono-label">Room privacy</span><div className="mt-2"><PrivacyPicker value={encrypted} onChange={setEncrypted} /></div></div>
      <button onClick={create} disabled={busy} className="btn-primary mt-8 w-full sm:w-auto" data-testid="create-room-submit-button">
        <Plus size={16} /> {busy ? "Opening…" : "Create room"}
      </button>
    </div>
  );
};

export const JoinRoomCard = () => {
  const nav = useNavigate();
  const [code, setCode] = useState("");
  const join = (e) => {
    e.preventDefault();
    const c = code.trim().split("/room/").pop().split(/[?#]/)[0].toUpperCase();
    if (c) nav(`/room/${c}`);
  };
  return (
    <form onSubmit={join} className="holo-panel p-6" data-testid="join-room-card">
      <div className="eyebrow">[02] Join by invite</div>
      <div className="mt-4 flex gap-2">
        <input className="field font-mono uppercase" placeholder="ROOM CODE OR LINK" value={code} onChange={(e) => setCode(e.target.value)} data-testid="dashboard-join-room-input" />
        <button className="btn-primary shrink-0" data-testid="dashboard-join-room-button"><LogIn size={16} /> Join</button>
      </div>
    </form>
  );
};
